import { useEffect, useMemo, useRef, useState } from "react";
import type { CartState } from "../lib/cart";
import type { GroceryItem } from "../lib/items";
import { MAX_CART_ITEMS } from "../lib/cart";

type TooltipSetter = (next: { open: boolean; text: string; x: number; y: number }) => void;

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function ScrollMashup({
  items,
  cart,
  onToggle,
  onTooltip,
}: {
  items: GroceryItem[];
  cart: CartState;
  onToggle: (id: string) => void;
  onTooltip: TooltipSetter;
}) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [w, setW] = useState(800);
  const [seed] = useState(() => Math.floor(Math.random() * 2 ** 32));
  const [ready, setReady] = useState(false);
  const hasFadedInRef = useRef(false);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      const cs = window.getComputedStyle(el);
      const padL = Number.parseFloat(cs.paddingLeft || "0") || 0;
      const padR = Number.parseFloat(cs.paddingRight || "0") || 0;
      setW(Math.max(320, r.width - padL - padR));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (hasFadedInRef.current) return;
    if (!w || items.length === 0) return;
    // Wait until after layout/paint, then fade in.
    const raf1 = window.requestAnimationFrame(() => {
      const raf2 = window.requestAnimationFrame(() => {
        setReady(true);
        hasFadedInRef.current = true;
      });
      // In case component unmounts between frames
      return () => window.cancelAnimationFrame(raf2);
    });
    return () => window.cancelAnimationFrame(raf1);
  }, [items.length, w]);

  const layout = useMemo(() => {
    // Guarantee no overlap by using a true lattice with fixed icon size + spacing.
    // We still randomize which item goes into which slot on each page load.
    const size = Math.max(72, Math.min(150, w / 6));
    const gap = Math.max(18, size * 0.18);
    const minDx = size + gap; // minimum center-to-center distance horizontally

    // Choose number of columns such that the computed spacing across the full width
    // never drops below minDx (so circles can't overlap).
    const cols = Math.max(2, Math.floor((w - size) / minDx) + 1);
    const dx = cols > 1 ? (w - size) / (cols - 1) : minDx;
    const dy = dx * 0.8660254038; // sqrt(3)/2 for hex packing

    const padTop = 18;
    const padBottom = 90;

    // Build enough lattice slots for all items:
    const slots: Array<{ left: number; top: number; size: number; z: number }> = [];
    let row = 0;
    while (slots.length < items.length) {
      const rowCols = row % 2 === 0 ? cols : Math.max(1, cols - 1);
      for (let col = 0; col < rowCols && slots.length < items.length; col++) {
        const cx = size / 2 + col * dx + (row % 2 ? dx / 2 : 0);
        const cy = padTop + size / 2 + row * dy;
        slots.push({
          left: cx - size / 2,
          top: cy - size / 2,
          size,
          z: row, // stable ordering; doesn't matter with no overlaps
        });
      }
      row++;
    }

    // Randomize assignment each reload:
    const rng = mulberry32(seed);
    const ids = items.map((it) => it.id);
    for (let i = ids.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [ids[i], ids[j]] = [ids[j], ids[i]];
    }

    const placedById = new Map<string, { left: number; top: number; size: number; z: number }>();
    for (let i = 0; i < ids.length; i++) {
      placedById.set(ids[i], slots[i]);
    }

    const height = Math.max(600, padTop + row * dy + size + padBottom);
    return { placedById, height };
  }, [items, seed, w]);

  const uniqueCount = Object.keys(cart).length;
  const cartFull = uniqueCount >= MAX_CART_ITEMS;

  return (
    <div ref={viewportRef} className={`mashupScroll ${ready ? "mashupReady" : "mashupHidden"}`}>
      <div className="mashupCanvas" style={{ height: layout.height }}>
        {items.map((item) => {
          const p = layout.placedById.get(item.id);
          if (!p) return null;
          const inCart = (cart[item.id] ?? 0) > 0;
          const disableAddVisuals = cartFull && !inCart;
          const tip = inCart ? "Remove from cart" : item.name;
          return (
            <button
              key={item.id}
              className={`mashupIcon ${inCart ? "mashupIconActive" : ""} ${
                disableAddVisuals ? "mashupIconDisabled" : ""
              }`}
              type="button"
              aria-disabled={disableAddVisuals}
              style={{
                left: p.left,
                top: p.top,
                width: p.size,
                height: p.size,
                zIndex: p.z,
                ["--img" as any]: `url(${item.imageUrl})`,
              }}
              onClick={(e) => {
                if (!inCart && uniqueCount >= MAX_CART_ITEMS) {
                  onTooltip({
                    open: true,
                    text: `Cart full`,
                    x: e.clientX,
                    y: e.clientY,
                  });
                  return;
                }
                onToggle(item.id);
                onTooltip({
                  open: true,
                  text: inCart ? "Remove from cart" : item.name,
                  x: e.clientX,
                  y: e.clientY,
                });
              }}
              onMouseEnter={(e) => {
                if (disableAddVisuals) return;
                onTooltip({ open: true, text: tip, x: e.clientX, y: e.clientY });
              }}
              onMouseMove={(e) => {
                if (disableAddVisuals) return;
                onTooltip({ open: true, text: tip, x: e.clientX, y: e.clientY });
              }}
              onMouseLeave={() => onTooltip({ open: false, text: "", x: 0, y: 0 })}
              aria-label={`${inCart ? "Remove" : "Add"} ${item.name} ${inCart ? "from" : "to"} cart`}
            >
              <img
                className="mashupImg"
                src={item.imageUrl}
                alt={item.name}
                loading="lazy"
                draggable={false}
                onDragStart={(ev) => ev.preventDefault()}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
